import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useTenant } from './TenantContext';
import authService from '../services/authService';

interface User {
  id: string;
  username: string;
  email?: string;
  name?: string;
  role: 'NURSE' | 'ADMIN' | 'DOCTOR' | 'SOCIAL_WORKER' | 'FACILITY_ADMIN' | 'CLIENT_ADMIN' | 'SUPER_ADMIN';
  tenantId?: string;
  facilityId?: string;
  clientId?: string;
  token?: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const { currentTenantId } = useTenant();

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      // Use real Keycloak authentication
      const { token, user: userData } = await authService.login(username, password);

      const authenticatedUser: User = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        token,
        ...(currentTenantId && { tenantId: currentTenantId }),
      };

      setUser(authenticatedUser);
      localStorage.setItem('user', JSON.stringify(authenticatedUser));

      return true;
    } catch (error: any) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('currentTenantId');
  };

  // Check if user is authenticated on initial load
  const checkAuthStatus = () => {
    if (!user) {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('authToken');

      if (storedUser && storedToken) {
        try {
          // Check if token is still valid
          if (!authService.isTokenExpired(storedToken)) {
            const parsedUser = JSON.parse(storedUser) as User;

            // Allow SUPER_ADMIN and CLIENT_ADMIN to login without tenant check
            // For other users, verify tenant match if both tenant IDs exist
            if (
              parsedUser.role === 'SUPER_ADMIN' ||
              parsedUser.role === 'CLIENT_ADMIN' ||
              !parsedUser.tenantId ||
              !currentTenantId ||
              parsedUser.tenantId === currentTenantId
            ) {
              setUser(parsedUser);
            } else {
              // If tenant mismatch, clear the stored user
              localStorage.removeItem('user');
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
            }
          } else {
            // Token expired, try to refresh
            authService.refreshToken().then((newToken) => {
              if (newToken) {
                const parsedUser = JSON.parse(storedUser) as User;
                parsedUser.token = newToken;
                setUser(parsedUser);
                localStorage.setItem('user', JSON.stringify(parsedUser));
              } else {
                // Refresh failed, clear everything
                localStorage.removeItem('user');
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
              }
            });
          }
        } catch (e) {
          console.error('Error parsing stored user', e);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
        }
      }
    }
  };

  // Check auth status when tenant changes
  useEffect(() => {
    checkAuthStatus();
  }, [currentTenantId]);

  // Auto-refresh token before expiry
  useEffect(() => {
    const interval = setInterval(
      () => {
        const token = localStorage.getItem('authToken');
        if (token && !authService.isTokenExpired(token)) {
          // Refresh token 5 minutes before expiry
          authService.refreshToken();
        }
      },
      5 * 60 * 1000
    ); // Check every 5 minutes

    return () => clearInterval(interval);
  }, []);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
